import {
  AlertSeverityEnum,
  Transaccion,
  TransaccionesPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

export enum TransaccionQueryKeyEnum {
  TRANSACCIONES = 'transacciones',
  TRANSACCION = 'transaccion',
}

///* tanStack query
export const useFetchTransacciones = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetTransaccionesParams> = {}) => {
  return useQuery({
    queryKey: [
      TransaccionQueryKeyEnum.TRANSACCIONES,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getTransacciones(params),
    enabled: enabled,
  });
};

export const useGetTransaccion = (id: string) => {
  return useQuery({
    queryKey: [TransaccionQueryKeyEnum.TRANSACCION, id],
    queryFn: () => getTransaccion(id),
    retry: false,
  });
};

export const useCreateTransaccion = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateTransaccionParams<T>) =>
      createTransaccion(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TransaccionQueryKeyEnum.TRANSACCIONES],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Transacción creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTransaccion = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateTransaccionParams<T>) =>
      updateTransaccion(params),
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [TransaccionQueryKeyEnum.TRANSACCIONES],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Transacción actualizada correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetTransaccionesParams = Partial<Transaccion> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateTransaccionParamsForm = Omit<Transaccion, 'id_transaccion'>;
export type CreateTransaccionParams<T> = T;
export type UpdateTransaccionParams<T> = {
  id: string;
  data: T;
};

export const getTransacciones = async (params?: GetTransaccionesParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<TransaccionesPagingRes>(
    `${urlBackend}api/v1/transaccion/?${queryParams}`,
    true
  );
};

export const getTransaccion = async (id: string) => {
  try {
    return await get<Transaccion>(
      `${urlBackend}api/v1/transaccion/${id}`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createTransaccion = async <T>(
  data: CreateTransaccionParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Transaccion>(`${urlBackend}api/v1/transaccion/`, data, true);
};

export const updateTransaccion = async <T>({
  id,
  data,
}: UpdateTransaccionParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Transaccion>(`${urlBackend}api/v1/transaccion/${id}/`, data, true);
};

///* action types ==============================
export type CreateTransaccionByPaymentRubroData = Pick<
  Transaccion,
  | 'cliente'
  | 'contrato'
  | 'rubro'
  | 'forma_pago'
  | 'observaciones'
  | 'comision'
  | 'total_pagado'
  | 'fecha_pago'
>;

export type ReversarTransaccionData = Pick<Transaccion, 'estado_reverso'>;
